import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableService,
} from '@web/shared/ui';
import { Route, RouteParam } from '@web/common';
import { Observable, of } from 'rxjs';
import { WarehouseProductCollectionItem } from '../../models/warehouse-product-collection-item';
import { WarehouseProductCollectionFilter } from '../../models/warehouse-product-collection-filter';
import { WarehouseProductListItemAction } from '../../enums/warehouse-product-list-item-action';
import { WarehouseProductListAction } from '../../enums/warehouse-product-list-action';
import { WarehouseProductDataSourceService } from '../../services/warehouse-product-data-source.service';
import { WarehouseProductListCustomCellTemplateComponent } from '../../components/warehouse-product-list-custom-cell-template/warehouse-product-list-custom-cell-template.component';
import { QuantityUnitProviderService } from '@web/shared/module/measurement';
import { WarehouseProductTypeProviderService } from '../../providers/warehouse-product-type-provider.service';

@Component({
  selector: 'app-warehouse-product-list',
  imports: [TableComponent, WarehouseProductListCustomCellTemplateComponent],
  templateUrl: './warehouse-product-list.component.html',
  styleUrl: './warehouse-product-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseProductListComponent {
  /* prettier-ignore */
  private readonly tableBuilder = inject(TableBuilderFactory<WarehouseProductCollectionItem, WarehouseProductCollectionFilter, WarehouseProductListItemAction, never, WarehouseProductListAction>).create();
  private readonly quantityUnitProvider = inject(QuantityUnitProviderService);
  /* prettier-ignore */
  private readonly warehouseProductTypeProvider = inject(WarehouseProductTypeProviderService);
  private readonly table = inject(TableService);
  /* prettier-ignore */
  private readonly warehouseProductDataSource = inject(WarehouseProductDataSourceService);

  public readonly owner = input.required<string>();
  public readonly downloadQuantityReportVisibility = input(false);

  protected readonly config = this.buildTable();

  private buildTable(): TableConfig<
    WarehouseProductCollectionItem,
    WarehouseProductCollectionFilter,
    WarehouseProductListItemAction,
    never,
    WarehouseProductListAction
  > {
    return this.tableBuilder
      .dataKey('id')
      .columns((builder) => [
        builder
          .initText('sku')
          .filterKey('sku')
          .sortKey('sku')
          .label('Numer artykułu (SKU)')
          .frozen(true)
          .build(),

        builder
          .initText('brand')
          .filterKey('brand')
          .sortKey('brand')
          .label('Marka kosmetyczna')
          .build(),

        builder //
          .initText('name')
          .filterKey('name')
          .sortKey('name')
          .width('30rem')
          .label('Nazwa')
          .build(),

        builder
          .initText('localization')
          .filterKey('localisation')
          .sortKey('localisation')
          .width('12rem')
          .label('Lokalizacja')
          .build(),

        builder
          .initText('quantity')
          .filterKey('quantity')
          .sortKey('quantity')
          .width('8rem')
          .label('Ilość')
          .build(),

        builder
          .initCustom('quantityUnit')
          .filterKey('quantityUnit')
          .sortKey('quantityUnit')
          .width('10rem')
          .label('Jednostka miary')
          .build(),

        builder
          .initText('durabilityInformation')
          .filterKey('durabilityInformation')
          .sortKey('durabilityInformation')
          .label('Okres przydatności')
          .build(),

        builder
          .initText('owner')
          .filterKey('owner')
          .sortKey('owner')
          .label('Właściciel')
          .build(),

        builder
          .initBoolean('promotion')
          .filterKey('promotion')
          .sortKey('promotion')
          .label('Produkt promocyjny')
          .build(),

        builder
          .initCustom('type')
          .filterKey('type')
          .sortKey('type')
          .width('12rem')
          .label('Rodzaj')
          .build(),

        builder
          .initDate('createdAt')
          .filterKey('createdAt')
          .sortKey('createdAt')
          .label('Data utworzenia')
          .build(),

        builder
          .initText('measure')
          .filterKey('measure')
          .sortKey('measure')
          .label('Miara towaru')
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initText('name')
          .build(),

        builder //
          .initText('sku')
          .build(),

        builder //
          .initText('localisation')
          .build(),

        builder //
          .initText('brand')
          .build(),

        builder //
          .initBoolean('promotion')
          .build(),

        builder
          .initSelect('type')
          .dictionary(() => this.warehouseProductTypeProvider.selectItems())
          .build(),

        builder //
          .initNumber('quantity')
          .build(),

        builder
          .initSelect('quantityUnit')
          .dictionary(() => this.quantityUnitProvider.selectItems())
          .build(),

        builder //
          .initText('durabilityInformation')
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),

        builder //
          .initText('measure')
          .build(),
      ])
      .actions((builder) => [
        builder
          .initCommand(WarehouseProductListAction.DownloadQuantityReport)
          .command(() => this.downloadQuantityReport())
          .label('Zestawienie stanów magazynowych')
          .visibility(() => of(this.downloadQuantityReportVisibility()))
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(WarehouseProductListItemAction.Preview)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.WarehouseProductDetails, {
                [RouteParam.Slug]: this.owner(),
                [RouteParam.Guid]: item.id,
              }),
          )
          .visibility((item) => item.canBeViewed)
          .tooltip('Podgląd')
          .icon(Icon.Eye)
          .build(),

        builder
          .initRouterLink(WarehouseProductListItemAction.Edit)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.WarehouseProductEdit, {
                [RouteParam.Slug]: this.owner(),
                [RouteParam.Guid]: item.id,
              }),
          )
          .visibility((item) => item.canBeEdited)
          .tooltip('Edytuj')
          .icon(Icon.Pencil)
          .build(),

        builder
          .initCommand(WarehouseProductListItemAction.DownloadLabel)
          .command((item) => this.downloadLabel(item))
          .visibility((item) => item.canBeLabelDownloaded)
          .tooltip('Pobierz etykietę produktu')
          .icon(Icon.CloudDownloadFill)
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad,
        ): Observable<TableDataPayload<WarehouseProductCollectionItem>> => {
          return this.warehouseProductDataSource.fetchCollection(
            this.owner(),
            lazyLoad,
          );
        },
      })
      .build();
  }

  private downloadLabel(item: WarehouseProductCollectionItem): void {
    this.warehouseProductDataSource
      .fetchProductLabel(this.owner(), item.id)
      .subscribe();
  }

  private downloadQuantityReport(): void {
    this.warehouseProductDataSource
      .fetchQuantityReport(this.owner())
      .subscribe();
  }
}
